<!-- Backdrop -->
<div
  class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4
         bg-black/50 backdrop-blur-sm"
  (click)="close()">

  <!-- Panel -->
  <div
    class="relative flex flex-col bg-white w-full sm:rounded-2xl sm:max-w-3xl
           max-h-screen sm:max-h-[90vh] overflow-hidden shadow-2xl"
    (click)="$event.stopPropagation()">

    <!-- ── Header ──────────────────────────────────────────────────────── -->
    <div class="flex items-center justify-between px-5 py-4 border-b border-pale-gray shrink-0">
      <div>
        <p class="text-[10px] font-bold uppercase tracking-[0.2em] text-action-blue">
          Disponibilidad
        </p>
        <h2 class="text-[16px] font-bold text-midnight-indigo leading-tight mt-0.5">
          {{ space.name }}
        </h2>
      </div>
      <button
        type="button"
        (click)="close()"
        class="p-2 rounded-xl text-steel-gray hover:text-midnight-indigo hover:bg-cloud-mist
               transition-colors focus:outline-none">
        <lucide-icon name="x" [size]="18" />
      </button>
    </div>

    <!-- ── Leyenda ──────────────────────────────────────────────────────── -->
    <div class="flex flex-wrap items-center gap-x-5 gap-y-1.5 px-5 py-2.5
                bg-cloud-mist border-b border-pale-gray shrink-0">
      <span class="flex items-center gap-1.5 text-[11px] text-steel-gray">
        <span class="h-3 w-3 rounded bg-emerald-100 border border-emerald-300 shrink-0"></span>
        Libre — pulsa para reservar
      </span>
      <span class="flex items-center gap-1.5 text-[11px] text-steel-gray">
        <span class="h-3 w-3 rounded bg-slate-200 border border-slate-300 shrink-0"></span>
        Horas ocupadas — queda disponibilidad
      </span>
      <span class="flex items-center gap-1.5 text-[11px] text-steel-gray">
        <span class="h-3 w-3 rounded bg-red-100 border border-red-300 shrink-0"></span>
        Completo o cerrado
      </span>
    </div>

    <!-- ── Cuerpo del calendario ────────────────────────────────────────── -->
    <div class="flex-1 overflow-y-auto px-5 py-6">

      @if (loading()) {
        <div class="flex flex-col items-center justify-center gap-3 py-16">
          <svg class="animate-spin h-6 w-6 text-action-blue" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
          <p class="text-[12px] text-steel-gray">Cargando disponibilidad…</p>
        </div>
      } @else if (error()) {
        <div class="flex flex-col items-center gap-3 py-12 text-center">
          <lucide-icon name="circle-alert" [size]="28" class="text-steel-gray" />
          <p class="text-[13px] text-steel-gray">{{ error() }}</p>
          <button type="button" (click)="ngOnInit()"
                  class="text-[12px] font-semibold text-action-blue hover:underline">
            Reintentar
          </button>
        </div>
      } @else {

        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">

          @for (month of months(); track month.label) {
            <div>
              <!-- Nombre del mes -->
              <p class="text-[13px] font-bold text-midnight-indigo mb-3">{{ month.label }}</p>

              <!-- Cabecera días de la semana -->
              <div class="grid grid-cols-7 mb-1">
                @for (d of DOW; track d) {
                  <div class="text-center text-[9px] font-bold uppercase tracking-widest
                              text-steel-gray py-1">
                    {{ d }}
                  </div>
                }
              </div>

              <!-- Celda por día -->
              <div class="grid grid-cols-7 gap-0.5">
                @for (day of month.days; track (day.date || $index)) {

                  @switch (day.status) {

                    @case ('padding') {
                      <div class="aspect-square"></div>
                    }

                    @case ('past') {
                      <div class="aspect-square flex items-center justify-center rounded-lg
                                  text-[11px] text-steel-gray/30 line-through select-none">
                        {{ day.dayNum }}
                      </div>
                    }

                    @case ('available') {
                      <!-- Verde: día completamente libre -->
                      <button
                        type="button"
                        (click)="onDayClick(day)"
                        class="aspect-square flex items-center justify-center rounded-lg
                               text-[11px] font-semibold text-emerald-700 bg-emerald-50
                               hover:bg-emerald-200 hover:scale-105
                               transition-all cursor-pointer
                               focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                        [class.ring-2]="day.isToday"
                        [class.ring-midnight-indigo]="day.isToday">
                        {{ day.dayNum }}
                      </button>
                    }

                    @case ('partial') {
                      <!-- Gris: alguna hora ocupada, queda disponibilidad -->
                      <button
                        type="button"
                        (click)="onDayClick(day)"
                        class="aspect-square flex items-center justify-center rounded-lg
                               text-[11px] font-medium text-slate-500 bg-slate-100
                               hover:bg-slate-200 hover:scale-105
                               transition-all cursor-pointer
                               focus:outline-none focus:ring-2 focus:ring-slate-400/40"
                        [class.ring-2]="day.isToday"
                        [class.ring-midnight-indigo]="day.isToday">
                        {{ day.dayNum }}
                      </button>
                    }

                    @case ('full') {
                      <!-- Rojo: día completo, no reservable -->
                      <div
                        class="aspect-square flex items-center justify-center rounded-lg
                               text-[11px] font-medium text-red-400 bg-red-50
                               cursor-not-allowed select-none"
                        [class.ring-2]="day.isToday"
                        [class.ring-midnight-indigo]="day.isToday">
                        {{ day.dayNum }}
                      </div>
                    }

                  }

                }
              </div>
            </div>
          }

        </div>
      }
    </div>

    <!-- ── Pie ──────────────────────────────────────────────────────────── -->
    <div class="px-5 py-3 border-t border-pale-gray bg-cloud-mist shrink-0">
      <p class="text-[11px] text-steel-gray text-center leading-relaxed">
        Pulsa un día disponible para continuar. Si no tienes cuenta, te pediremos
        que te registres antes de confirmar la reserva.
      </p>
    </div>

  </div>
</div>
